import React, { useEffect, useState } from "react";
import {
  getAllMenuItems,
  getMenuUpdate,
  createMenuItem,
  deleteMenuItem,
} from "../../../../api/ApiConfig";
import "./MenuScreen.css";
import { FaGreaterThan, FaLessThan, FaRegFolderOpen } from "react-icons/fa";
import toast from "react-hot-toast";
import UpdateMenuModal from "./UpdateModal";
import CreateMenuModal from "./CreateModal";
import DeleteMenuModal from "./DeleteModal";

const MenuItemsList = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteSelectedItem, setDeleteSelectedItem] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 1,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const response = await getAllMenuItems({
        page: pagination.page,
        page_size: pagination.page_size,
        search_query: searchQuery || undefined,
      });

      if (response.items) {
        setMenuItems(response.items);
        setPagination({
          page: response.page,
          page_size: response.size,
          total_pages: response.total_pages,
        });
      } else {
        toast.error("Failed to fetch menu items");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenuItems();
  }, [pagination.page, searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.total_pages) {
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  return (
    <div className="menus-container">
      {/* HEADER */}
      <div className="menus-header">
        <h4 className="menus-title">Menu Items</h4>
        <button className="view-btn" onClick={() => setShowCreateModal(true)}>
          Create New Link
        </button>
      </div>

      {/* SEARCH */}
      <div className="menus-search-box">
        <input
          type="text"
          placeholder="Search menu..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="search-field-global-search"
        />
      </div>

      {/* CONTENT */}
      {loading ? (
        <p>Loading...</p>
      ) : menuItems.length === 0 ? (
        <div className="menus-no-items">
          <FaRegFolderOpen size={40} />
          <p>No menu items found</p>
        </div>
      ) : (
        <>
          <div className="menus-table-wrapper">
            <table className="menus-table">
              <thead>
                <tr>
                  <th>SL.NO</th>
                  <th>Menu Name</th>
                  <th>Full Path Name</th>
                  <th>Menu URL</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {menuItems.map((item) => (
                  <tr key={item.slno}>
                    <td>{item.row_no}</td>
                    <td>{item.label}</td>

                    <td className="wrap-cell">
                      {item.full_path_label}
                    </td>

                    <td className="wrap-cell url-cell">
                      <a href={item.url} target="_blank" rel="noreferrer">
                        {item.url}
                      </a>
                    </td>

                    <td className="action-cell">
                      <button
                        className="view-btn"
                        onClick={() => setSelectedItem(item)}
                      >
                        Update
                      </button>
                      <button
                        className="view-btn danger-btn"
                        onClick={() => {
                          setDeleteSelectedItem(item);
                          setShowDeleteModal(true);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="menu-pagination">
            <button
              onClick={handlePrevPage}
              disabled={pagination.page === 1}
              className="view-btn"
            >
              <FaLessThan />
            </button>

            <span>
              Page {pagination.page} of {pagination.total_pages}
            </span>

            <button
              onClick={handleNextPage}
              disabled={pagination.page === pagination.total_pages}
              className="view-btn"
            >
              <FaGreaterThan />
            </button>
          </div>
        </>
      )}

      {/* MODALS */}
      {selectedItem && (
        <UpdateMenuModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onSave={async (data) => {
            await getMenuUpdate(data);
            toast.success("Menu updated successfully");
            fetchMenuItems();
            setSelectedItem(null);
          }}
        />
      )}

      {showCreateModal && (
        <CreateMenuModal
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            await createMenuItem(data);
            toast.success("Menu created successfully");
            fetchMenuItems();
            setShowCreateModal(false);
          }}
        />
      )}

      {deleteSelectedItem && (
        <DeleteMenuModal
          item={deleteSelectedItem}
          onClose={() => {
            setDeleteSelectedItem(null);
            setShowDeleteModal(false);
          }}
          onRemove={async (data) => {
            await deleteMenuItem(data);
            toast.success("Menu deleted successfully");
            fetchMenuItems();
            setDeleteSelectedItem(null);
            setShowDeleteModal(false);
          }}
        />
      )}
    </div>
  );
};

export default MenuItemsList;
